import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex items-center justify-center py-8 bg-white p-6"
}
const _hoisted_2 = { class: "columns-2 mt-4" }
const _hoisted_3 = { class: "columns-2 mt-4 flex gap-4 items-end" }
const _hoisted_4 = { class: "text-gray-700 font-medium text-sm" }
const _hoisted_5 = { class: "relative" }
const _hoisted_6 = { class: "columns-2 mt-4" }
const _hoisted_7 = { class: "columns-2 mt-4" }
const _hoisted_8 = { class: "flex justify-self-end mt-4" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "font-semibold text-sm" }
const _hoisted_11 = {
  key: 0,
  class: "flex items-center justify-center gap-x-1"
}
const _hoisted_12 = { key: 1 }

import { countryOptions } from "@/helper/countries";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import { ref, watch, computed, onMounted } from "vue";
import i18n from "@/plugins/i18n";
import { useSchoolStore } from "@/stores/schoolStore";
import { useSchoolGroupStore } from "@/stores/schoolGroupsStore";
import { School } from "@/interfaces/school.interface";
import { Admin } from "@/interfaces/admin.interface";
import { useFilterStore } from "@/stores/filterStore";
import { createDefaultSchoolObject }  from "@/modules/main/components/constants/defaults"
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/vue";
import  SchoolChampionCreation  from "@/modules/main/components/internal-tools/SchoolChampionCreation.vue";
import { CheckCircleIcon } from "@heroicons/vue/20/solid";
import { showToast } from '@/utils/toastUtils';
import { useI18n } from 'vue-i18n'

interface SearchOption {
  id: string;
  name: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SchoolDetailsComponent',
  setup(__props) {

const { t } = useI18n();


function notifySuccessSchoolDetails() {
  showToast({
    text: t('internal_tools.success.school_message'),
    type: 'success',
    icon: CheckCircleIcon,
  })
}

const schoolStore = useSchoolStore();
const schoolGroupStore = useSchoolGroupStore();
const filterStore = useFilterStore();

const updateSuccess = ref(false);
const responseMessage = ref("");
const updatedFields = ref<{ school?: any; champion?: any }>({});
const searchQuery = ref("");
const selectedOption = ref<SearchOption | null>(null);
const placeholder = ref(i18n.global.t("internal_tools.select").toString());
const schoolObject = ref(createDefaultSchoolObject());
const initialSchoolObject =  ref(createDefaultSchoolObject());

// Computed filtered options based on `searchQuery`
const filteredOptions = computed(() =>
  searchQuery.value === ""
    ? countryOptions
    : countryOptions.filter((o) =>
        o.name.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
);

// Computed property for FormKit select options
const localeOptions = computed(() =>
  schoolStore.getLocales.map((locale) => ({
    label: locale.name, // Display name
    value: locale.code, // Code as value
  }))
);

// Function to check for changes when saving
const handleNext = async () => {
  
  const schoolChanges = findChangedFields(
    initialSchoolObject.value,
    schoolObject.value
  );

  updatedFields.value = {
    ...(Object.keys(schoolChanges).length ? { school: schoolChanges } : {})
  };

  // udpate school details
  if (updatedFields.value.school) {
    if (schoolStore.getCurrentSchool) {

      const result = await schoolStore.updateSchool(
        filterStore.selectedFilters.school,
        updatedFields.value.school
      );
      
      if (result) {
        // Fetch updated school structure
        updateSchoolDetails();
        responseMessage.value = i18n.global
          .t("internal_tools.success.school_message")
          .toString();
        updateSuccess.value = true;
        notifySuccessSchoolDetails();
    
      }
    }
  } 

};

const hasSchoolChanges = computed(() => {
  const changes = findChangedFields(initialSchoolObject.value, schoolObject.value);
  return Object.keys(changes).length > 0;
});

const updateSchoolDetails = async () => {
  const currentSchool = schoolStore.getCurrentSchool;

  if (currentSchool) {
    schoolObject.value = { ...schoolStore.currentSchool };
    initialSchoolObject.value = JSON.parse(
      JSON.stringify(schoolStore.currentSchool)
    );
  }
};

const findChangedFieldsAdmin = (initialData: Admin[], currentData: Admin[]) => {
  return currentData
    .map((currentAdmin, index) => {
      const initialAdmin = initialData[index];
      const changes: { [key: string]: any } = {};

      for (const key in currentAdmin) {
        let currentValue = currentAdmin[key as keyof Admin];
        let initialValue = initialAdmin[key as keyof Admin];

        if (typeof currentValue === "string")
          currentValue = currentValue.trim();
        if (typeof initialValue === "string")
          initialValue = initialValue.trim();

        if (
          (currentValue === null ||
            currentValue === "" ||
            (Array.isArray(currentValue) && currentValue.length === 0)) &&
          (initialValue === null ||
            initialValue === "" ||
            (Array.isArray(initialValue) && initialValue.length === 0))
        ) {
          continue;
        } else if (
          typeof currentValue === "object" &&
          currentValue !== null &&
          !Array.isArray(currentValue)
        ) {
          const nestedChanges = findChangedFieldsAdmin(
            [initialValue] as unknown as Admin[],
            [currentValue] as unknown as Admin[]
          );
          if (Object.keys(nestedChanges).length > 0) {
            changes[key] = nestedChanges;
          }
        } else if (
          JSON.stringify(currentValue) !== JSON.stringify(initialValue)
        ) {
          changes[key] = currentValue;
        }
      }

      // Include `_id` if there are any changes for this admin
      if (Object.keys(changes).length > 0) {
        changes["_id"] = currentAdmin._id;
      }

      return Object.keys(changes).length > 0 ? changes : null;
    })
    .filter((change) => change !== null);
};

const findChangedFields = (initialData: School, currentData: School) => {
  const changes: { [key: string]: any } = {};

  for (const key in currentData) {
    let currentValue = currentData[key as keyof School];
    let initialValue = initialData[key as keyof School];

    // Trim strings before comparing
    if (typeof currentValue === "string") currentValue = currentValue.trim();
    if (typeof initialValue === "string") initialValue = initialValue.trim();

    if (
      (currentValue === null ||
        currentValue === "" ||
        (Array.isArray(currentValue) && currentValue.length === 0)) &&
      (initialValue === null ||
        initialValue === "" ||
        (Array.isArray(initialValue) && initialValue.length === 0))
    ) {
      // Skip if both initial and current values are null, empty string, or empty array
      continue;
    } else if (
      typeof currentValue === "object" &&
      currentValue !== null &&
      !Array.isArray(currentValue)
    ) {
      // Recursively check nested objects
      const nestedChanges = findChangedFields(
        initialValue as unknown as School,
        currentValue as unknown as School
      );
      if (Object.keys(nestedChanges).length > 0) {
        (changes as { [key: string]: any })[key] = nestedChanges;
      }
    } else if (JSON.stringify(currentValue) !== JSON.stringify(initialValue)) {
      // Add only changed or non-empty values to changes
      changes[key] = currentValue;
    }
  }

  return changes;
};

// Change coutry casing
const formatCountryName = (country: string) => {
  //check if the country is in the countryOptions
  const countryExist = countryOptions.find((o) => o.id === country);

  //if the country is not in the countryOptions, return the selectedOption value
  if (!countryExist) {
    return selectedOption.value?.name;
  }

  return country
    ? country
        .split("-")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    : "";
};

onMounted(async () => {

  const schoolGroupId = schoolStore.currentSchool?.schoolGroup;

  if (schoolGroupId) {
    await schoolGroupStore.fetchAllSchoolChampions(schoolGroupId);
  }

  const initialCountryId = schoolStore.getCurrentSchool?.location.country;


  // // Call functions onMounted to trigger the same logic as the watchers
  updateSchoolDetails();

  selectedOption.value =
    countryOptions.find((o) => o.id === initialCountryId) || null;
  searchQuery.value = selectedOption.value ? selectedOption.value.name : "";
});

// Watch `selectedOption` to update `searchQuery` and bind to `schoolObject` country ID
watch(selectedOption, (newOption) => {
  searchQuery.value = newOption ? newOption.name : "";
  schoolObject.value.location.country = newOption?.id || "";
});

// Define watchers
watch(() => schoolStore.getCurrentSchool, updateSchoolDetails);

return (_ctx: any,_cache: any) => {
  const _component_spinner_loader = _resolveComponent("spinner-loader")!
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(schoolStore).loading === true)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_component_spinner_loader, { color: 'text-blue-600 dark:text-blue-600' })
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", null, [
      _createVNode(_component_FormKit, {
        type: "form",
        id: "login-form",
        actions: false,
        onSubmit: handleNext
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_FormKit, {
              type: "text",
              label: _ctx.$t('internal_tools.form.school_location'),
              "outer-class": "primary-select primary-select-clear",
              "inner-class": "bg-white",
              placeholder: _ctx.$t('internal_tools.form.school_location'),
              modelValue: schoolObject.value.location.street,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((schoolObject.value.location.street) = $event))
            }, null, 8, ["label", "placeholder", "modelValue"]),
            _createVNode(_component_FormKit, {
              type: "text",
              label: _ctx.$t('internal_tools.form.domain_name'),
              "outer-class": "primary-select",
              placeholder: _ctx.$t('internal_tools.form.domain_name'),
              modelValue: schoolObject.value.domain,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((schoolObject.value.domain) = $event))
            }, null, 8, ["label", "placeholder", "modelValue"])
          ]),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_FormKit, {
              class: "w-1/2",
              type: "text",
              label: _ctx.$t('internal_tools.form.city'),
              "outer-class": "primary-select w-full",
              placeholder: _ctx.$t('internal_tools.form.city_name'),
              modelValue: schoolObject.value.location.city,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((schoolObject.value.location.city) = $event))
            }, null, 8, ["label", "placeholder", "modelValue"]),
            _createVNode(_unref(Combobox), {
              class: "w-1/2",
              as: "div",
              modelValue: selectedOption.value,
              "onUpdate:modelValue": [
                _cache[5] || (_cache[5] = ($event: any) => ((selectedOption).value = $event)),
                _cache[6] || (_cache[6] = ($event: any) => (searchQuery.value = ''))
              ],
              label: _ctx.$t('internal_tools.form.city')
            }, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t("internal_tools.form.country_title")), 1),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(ComboboxInput), {
                    class: "w-full rounded-md border-0 bg-white py-[18px] pl-3 pr-10 text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 font-normal text-base",
                    placeholder: placeholder.value,
                    onChange: _cache[3] || (_cache[3] = ($event: any) => (searchQuery.value = $event.target.value)),
                    onBlur: _cache[4] || (_cache[4] = ($event: any) => (searchQuery.value = '')),
                    "display-value": 
                    () =>
                    formatCountryName(schoolObject.value.location.country) || ''
                
                  }, null, 8, ["placeholder", "display-value"]),
                  _createVNode(_unref(ComboboxButton), { class: "absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(ChevronUpDownIcon), {
                        class: "h-5 w-5 text-gray-400",
                        "aria-hidden": "true"
                      })
                    ]),
                    _: 1
                  }),
                  (filteredOptions.value.length > 0)
                    ? (_openBlock(), _createBlock(_unref(ComboboxOptions), {
                        key: 0,
                        class: "dropdown-options absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (option) => {
                            return (_openBlock(), _createBlock(_unref(ComboboxOption), {
                              key: option.id,
                              value: option,
                              as: "template"
                            }, {
                              default: _withCtx(({ active, selected }) => [
                                _createElementVNode("li", {
                                  class: _normalizeClass([
                        'relative cursor-default select-none py-2 pl-8 pr-4',
                        active ? 'bg-blue-600 text-white' : 'text-gray-900',
                    ])
                                }, [
                                  _createElementVNode("span", {
                                    class: _normalizeClass([
                        'block truncate',
                        selected && 'font-semibold',
                        ])
                                  }, _toDisplayString(option.name), 3),
                                  selected
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 0,
                                        class: _normalizeClass([
                        'absolute inset-y-0 left-0 flex items-center pl-1.5',
                        active ? 'text-white' : 'text-blue-600',
                        ])
                                      }, [
                                        _createVNode(_unref(CheckIcon), {
                                          class: "h-5 w-5",
                                          "aria-hidden": "true"
                                        })
                                      ], 2))
                                    : _createCommentVNode("", true)
                                ], 2)
                              ]),
                              _: 2
                            }, 1032, ["value"]))
                          }), 128))
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            }, 8, ["modelValue", "label"])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_component_FormKit, {
              type: "select",
              name: "locale",
              options: localeOptions.value,
              label: _ctx.$t('internal_tools.form.language_title'),
              "outer-class": "primary-select",
              placeholder: _ctx.$t('internal_tools.form.language'),
              style: { color: 'text-gray-500', height: '3.5rem' },
              modelValue: schoolObject.value.preferredLang,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((schoolObject.value.preferredLang) = $event))
            }, null, 8, ["options", "label", "placeholder", "modelValue"]),
            _createVNode(_component_FormKit, {
              type: "text",
              label: _ctx.$t('internal_tools.form.emis_number'),
              "outer-class": "primary-select",
              placeholder: _ctx.$t('internal_tools.form.emis_number'),
              modelValue: schoolObject.value.emisNumber,
              "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((schoolObject.value.emisNumber) = $event))
            }, null, 8, ["label", "placeholder", "modelValue"])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_FormKit, {
              type: "text",
              label: _ctx.$t('internal_tools.form.start_month'),
              "outer-class": "primary-select",
              placeholder: _ctx.$t('internal_tools.form.month'),
              modelValue: schoolObject.value.academicYearStart,
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((schoolObject.value.academicYearStart) = $event))
            }, null, 8, ["label", "placeholder", "modelValue"]),
            _createVNode(_component_FormKit, {
              type: "text",
              label: _ctx.$t('internal_tools.form.end_month'),
              "outer-class": "primary-select",
              placeholder: _ctx.$t('internal_tools.form.month'),
              modelValue: schoolObject.value.academicYearEnd,
              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((schoolObject.value.academicYearEnd) = $event))
            }, null, 8, ["label", "placeholder", "modelValue"])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("button", {
              class: _normalizeClass(["px-[14px] py-[10px] text-white border focus:outline-none focus:ring-2 rounded-lg", _unref(schoolStore).updateLoader || !hasSchoolChanges.value
                ? 'cursor-not-allowed !bg-blue-300 border-blue-300 !opacity-100'
                : 'cursor-pointer bg-blue-600 border border-blue-600']),
              disabled: !hasSchoolChanges.value || _unref(schoolStore).updateLoader,
              type: "submit"
            }, [
              _createElementVNode("h1", _hoisted_10, [
                (
                 _unref(schoolStore).updateLoader
                )
                  ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                      _createVNode(_component_spinner_loader, { color: 'text-white dark:text-white' }),
                      _createTextVNode(" " + _toDisplayString(_ctx.$t("internal_tools.form.btnSaving")), 1)
                    ]))
                  : (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(_ctx.$t("internal_tools.form.btnSave")), 1))
              ])
            ], 10, _hoisted_9)
          ])
        ]),
        _: 1
      }),
      _createVNode(SchoolChampionCreation, { showExistingChampions: true })
    ])
  ], 64))
}
}

})